/**
 * NOTE: This file is part of the legacy Notes feature.
 * User-facing text has been renamed to "Documentation" for clarity.
 * For the new Notes feature, check the Hub implementation.
 */

import { fetchApi } from "@/lib/api/index.ts";
import { useCurrentUser, User } from "@/lib/api/user.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface Note {
  id: string;
  createdBy: User;
  title: string;
  content: string;
  mentorshipId: string;
  metadata: {
    menteeId?: string;
    [key: string]: unknown;
  };
  reminder?: string | null;
  createdAt: string;
  updatedAt: string;
  sharedWith?: string[];
}

export const useNotes = (mentorshipId?: string) => {
  return useQuery<Note[]>({
    queryKey: ["notes", mentorshipId],
    queryFn: async () => {
      const notes = await fetchApi<{ notes: Note[] }>({
        url: `/v2/notes/mentorship/${mentorshipId}`,
        method: "GET",
      });

      if (!notes.data) throw new Error("No notes found");

      return notes.data.notes;
    },
    enabled: !!mentorshipId,
  });
};

export interface NoteCreateSchema {
  title: string;
  content: string;
  metadata?: Record<string, unknown>;
  reminder?: string | null;
  mentorshipId: string;
}

export const useCreateNote = () => {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  return useMutation<
    Note,
    Error,
    NoteCreateSchema,
    {
      previousNotes: Note[];
    }
  >({
    mutationKey: ["notes-create"],
    mutationFn: async (data) => {
      const note = await fetchApi<{ note: unknown }>({
        url: "/v2/notes",
        method: "POST",
        data,
      });

      if (!note.data?.note) throw new Error("Note not created");

      // This needs to be done as BE sends the note as a different shape
      return {
        ...note.data.note,
        createdBy: user, // User is guaranteed to be defined
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Note;
    },
    onMutate: async (data) => {
      const queryKey = ["notes", data.mentorshipId];
      await queryClient.cancelQueries({
        queryKey,
      });

      const previousNotes = queryClient.getQueryData<Note[]>(queryKey);

      queryClient.setQueryData<Note[]>(queryKey, (oldNotes) => {
        return [
          {
            ...data,
            metadata: {
              ...data.metadata,
            },
            id: window.crypto.randomUUID(),
            createdBy: user!, // User is guaranteed to be defined
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          } satisfies Note,
          ...(oldNotes || []),
        ];
      });

      return { previousNotes: previousNotes || [] };
    },
    onError: (_, data, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(
          ["notes", data.mentorshipId],
          context.previousNotes,
        );
      }
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["notes", variables.mentorshipId],
      });
    },
  });
};

export interface NoteUpdateSchema extends Partial<NoteCreateSchema> {
  id: string;
  mentorshipId: string;
}

export const useEditNote = () => {
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  return useMutation<
    Note,
    Error,
    NoteUpdateSchema,
    {
      previousNotes: Note[];
    }
  >({
    mutationKey: ["notes-edit"],
    mutationFn: async (data) => {
      const note = await fetchApi<{ note: unknown }>({
        url: `/v2/notes/${data.id}`,
        method: "PATCH",
        data: {
          title: data.title,
          content: data.content,
          metadata: data.metadata,
          reminder: data.reminder,
        },
      });

      if (!note.data?.note) throw new Error("Note not updated");

      return {
        ...note.data.note,
        createdBy: user, // User is guaranteed to be defined
        updatedAt: new Date().toISOString(),
      } as Note;
    },
    onMutate: async (data) => {
      const queryKey = ["notes", data.mentorshipId];

      await queryClient.cancelQueries({
        queryKey,
      });

      const previousNotes = queryClient.getQueryData<Note[]>(queryKey);

      queryClient.setQueryData<Note[]>(queryKey, (oldNotes) => {
        return (oldNotes || []).map((note) => {
          if (note.id === data.id) {
            return {
              ...note,
              ...data,
              updatedAt: new Date().toISOString(),
              metadata: {
                ...data.metadata,
              },
            } satisfies Note;
          }

          return note;
        });
      });

      return { previousNotes: previousNotes || [] };
    },
    onError: (_, data, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(
          ["notes", data.mentorshipId],
          context.previousNotes,
        );
      }
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["notes", variables.mentorshipId],
      });
    },
  });
};

interface NoteDeleteSchema {
  id: string;
  mentorshipId: string;
}

export const useDeleteNote = () => {
  const queryClient = useQueryClient();

  return useMutation<
    NoteDeleteSchema,
    Error,
    NoteDeleteSchema,
    {
      previousNotes: Note[];
    }
  >({
    mutationKey: ["notes-delete"],
    mutationFn: async (data) => {
      await fetchApi({
        url: `/v2/notes/${data.id}`,
        method: "DELETE",
      });

      return data;
    },
    onMutate: async (data) => {
      const queryKey = ["notes", data.mentorshipId];
      await queryClient.cancelQueries({
        queryKey,
      });

      const previousNotes = queryClient.getQueryData<Note[]>(queryKey);

      queryClient.setQueryData<Note[]>(queryKey, (oldNotes) => {
        return (oldNotes || []).filter((note) => note.id !== data.id);
      });

      return { previousNotes: previousNotes || [] };
    },
    onError: (_, data, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(
          ["notes", data.mentorshipId],
          context.previousNotes,
        );
      }
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["notes", variables.mentorshipId],
      });
    },
  });
};

interface NoteShareSchema {
  id: string;
  shareWithId: string;
  mentorshipId: string;
}

export const useShareNote = () => {
  const queryClient = useQueryClient();

  return useMutation<
    NoteShareSchema,
    Error,
    NoteShareSchema,
    {
      previousNotes: Note[];
    }
  >({
    mutationKey: ["notes-share"],
    mutationFn: async (data) => {
      await fetchApi({
        url: `/v2/notes/${data.id}/share`,
        method: "POST",
        data: {
          shareWithId: data.shareWithId,
        },
      });

      return data;
    },
    onMutate: async (data) => {
      const queryKey = ["notes", data.mentorshipId];
      await queryClient.cancelQueries({
        queryKey,
      });

      const previousNotes = queryClient.getQueryData<Note[]>(queryKey);

      queryClient.setQueryData<Note[]>(queryKey, (oldNotes) => {
        return (oldNotes || []).map((note) => {
          if (note.id === data.id) {
            return {
              ...note,
              sharedWith: [
                ...new Set([...(note.sharedWith || []), data.shareWithId]),
              ],
              updatedAt: new Date().toISOString(),
            } satisfies Note;
          }

          return note;
        });
      });

      return { previousNotes: previousNotes || [] };
    },
    onError: (_, data, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(
          ["notes", data.mentorshipId],
          context.previousNotes,
        );
      }
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["notes", variables.mentorshipId],
      });
    },
  });
};

export const useUnshareNote = () => {
  const queryClient = useQueryClient();

  return useMutation<
    NoteShareSchema,
    Error,
    NoteShareSchema,
    {
      previousNotes: Note[];
    }
  >({
    mutationKey: ["notes-unshare"],
    mutationFn: async (data) => {
      await fetchApi({
        url: `/v2/notes/${data.id}/share`,
        method: "DELETE",
        data: {
          shareWithId: data.shareWithId,
        },
      });

      return data;
    },
    onMutate: async (data) => {
      const queryKey = ["notes", data.mentorshipId];
      await queryClient.cancelQueries({
        queryKey,
      });

      const previousNotes = queryClient.getQueryData<Note[]>(queryKey);

      queryClient.setQueryData<Note[]>(queryKey, (oldNotes) => {
        return (oldNotes || []).map((note) => {
          if (note.id === data.id) {
            return {
              ...note,
              sharedWith: (note.sharedWith || []).filter(
                (id) => id !== data.shareWithId,
              ),
              updatedAt: new Date().toISOString(),
            } satisfies Note;
          }

          return note;
        });
      });

      return { previousNotes: previousNotes || [] };
    },
    onError: (_, data, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(
          ["notes", data.mentorshipId],
          context.previousNotes,
        );
      }
    },
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["notes", variables.mentorshipId],
      });
    },
  });
};
